import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Download, Eye, Trash2, Edit, Filter, Trophy, PieChart, Users, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../utils/helpers';

const ExpenseReport = ({ expenses, walletTransactions = [], games, tournaments, teams, players, clubName, onDeleteExpense, onEditExpense, onApproveExpense, onDenyExpense, onMarkReimbursed, onSettleTournamentFee, onMarkClubCovered, onMarkExternalPlayerPaid, onUnmarkExternalPlayerPaid, onBack, currentUserEmail }) => {
  const [selectedTournament, setSelectedTournament] = useState('all');
  const [paymentModal, setPaymentModal] = useState(null); // { expenseId, playerId, playerName, amount }
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [selectedPlayerForChart, setSelectedPlayerForChart] = useState('all');
  const [showCharts, setShowCharts] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Helper to toggle row expansion
  const toggleRow = (expenseId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(expenseId)) {
      newExpanded.delete(expenseId);
    } else {
      newExpanded.add(expenseId);
    }
    setExpandedRows(newExpanded);
  };

  // Helper to check if a player is external
  const isExternalPlayer = (playerId) => {
    if (!playerId) return false;
    // Check by ID pattern
    if (playerId.startsWith('guest-') || playerId.startsWith('guest_') || playerId.startsWith('player_')) {
      return true;
    }
    // Check by player data
    if (teams) {
      for (const team of teams) {
        if (team.players) {
          const player = team.players.find(p => p.id === playerId || p.userId === playerId);
          if (player && (player.isExternal || player.isManual || player.isGuest)) {
            return true;
          }
        }
      }
    }
    return false;
  };

  // Filter expenses by tournament
  const getFilteredExpenses = () => {
    if (selectedTournament === 'all') {
      return expenses;
    }

    return expenses.filter(exp =>
      exp.tournamentId === selectedTournament ||
      (exp.gameId && games.find(g => g.id === exp.gameId && g.tournamentId === selectedTournament))
    );
  };

  const filteredExpenses = getFilteredExpenses();

  // ONLY show actual expenses (money spent), NOT Initial Donations (revenue)
  const actualExpenses = filteredExpenses.filter(e => !e.isRegistrationFee && !e.isInitialDonation);
  const totalExpenses = actualExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  const gameExpenses = actualExpenses;

  // Separate tournament fees from game expenses
  const tournamentFees = gameExpenses.filter(e => e.isTournamentFee && e.tournamentId && !e.gameId);
  const regularExpenses = gameExpenses.filter(e => !e.isTournamentFee || e.gameId);

  // Helper to resolve player name
  const resolvePlayerName = (playerId) => {
    // 1. Try finding in direct players list (includes unassigned)
    if (players) {
      const found = players.find(p => p.id === playerId || p.userId === playerId);
      if (found) return found.name;
    }

    // 2. Fallback to finding in teams
    if (teams) {
      for (const team of teams) {
        if (team.players) {
          const found = team.players.find(p => p.id === playerId || p.userId === playerId);
          if (found) return found.name;
        }
      }
    }
    return 'Unknown Player';
  };

  // Helper to find approver for Initial Donation from Wallet Transactions
  const getDonationApprover = (expense) => {
    // Only applies to initial donations
    if (!expense.isInitialDonation && !expense.isRegistrationFee) return null;

    // Find the player ID (expense.splitAmong[0] typically for initial donation)
    const playerId = expense.splitAmong?.[0] || expense.playerEmail;
    if (!playerId) return null;

    // Look for successful payment transaction for this user
    // We look for 'payment' type, amount < 0 (payment out), and matching user/amount
    const matchingTxn = walletTransactions.find(t =>
      t.type === 'payment' &&
      t.status === 'approved' &&
      (t.userId === playerId || (t.playerEmail && expense.playerEmail && t.playerEmail.toLowerCase() === expense.playerEmail.toLowerCase()))
      // Optional: check amount match if exact correlation needed, but usually any approved payment implies treasurer action
      // Math.abs(t.amount) === expense.amount
    );

    if (matchingTxn) {
      return matchingTxn.approvedByName || "N/A";
    }
    return null;
  };

  // Render Status Badge
  const renderStatusBadge = (expense) => {
    if (expense.approvalStatus === 'approved') {
      let approverName = expense.approvedByName;
      let label = "Approved";

      // Special logic for Initial Donations: Check Wallet Transaction for real approver
      if (expense.isInitialDonation || expense.isRegistrationFee) {
        const txnApprover = getDonationApprover(expense);
        if (txnApprover) {
          approverName = txnApprover;
        } else if (expense.approvedBy === 'system') {
          // Default to N/A if strictly system with no linked payment yet
          approverName = "N/A";
        }
      } else if (expense.approvedBy === 'system') {
        approverName = "N/A";
      }

      return (
        <div className="flex flex-col items-start gap-1">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle size={12} /> {label}
          </span>
          {approverName ? (
            <span className="text-[10px] text-gray-500">by {approverName}</span>
          ) : null}
        </div>
      );
    }
    if (expense.approvalStatus === 'denied') {
      return (
        <div className="flex flex-col items-start gap-1">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={12} /> Denied
          </span>
          {expense.deniedByName && (
            <span className="text-[10px] text-gray-500">by {expense.deniedByName}</span>
          )}
        </div>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle size={12} /> Pending
      </span>
    );
  };

  // Render Table Row
  const renderExpenseRow = (expense, type) => {
    const isExpanded = expandedRows.has(expense.id);

    // Date Logic: Prefer 'date', fallback to 'createdAt', then 'No date'
    let dateDisplay = 'No date';
    if (expense.date) {
      dateDisplay = formatDate(expense.date);
    } else if (expense.createdAt) {
      dateDisplay = formatDate(expense.createdAt);
    }

    const isSettled = expense.settled;

    // Context Name (Tournament or Game) or Player Name for Donation
    let contextName = '-';
    if (type === 'tournament') {
      const tourney = tournaments?.find(t => t.id === expense.tournamentId);
      contextName = tourney?.name || 'Unknown Tournament';
    } else if (type === 'donation') {
      // For donations, Context becomes "Player"
      contextName = expense.playerName || (expense.splitAmong?.length > 0 ? resolvePlayerName(expense.splitAmong[0]) : 'Unknown Player');
    } else {
      const game = games?.find(g => g.id === expense.gameId);
      contextName = game ? `${game.opponent} (${game.date})` : 'Other';
    }

    // Split Summary
    let splitSummary = '-';
    if (expense.clubCovered) {
      splitSummary = <span className="text-green-600 font-medium">Club Covered</span>;
    } else if (expense.splitAmong?.length > 0) {
      splitSummary = (
        <span className="cursor-pointer text-blue-600 hover:text-blue-800 decoration-dotted underline" onClick={() => toggleRow(expense.id)}>
          {expense.splitAmong.length} Player{expense.splitAmong.length !== 1 ? 's' : ''} (View)
        </span>
      );
    } else if (expense.isTournamentFee && !expense.settled) {
      splitSummary = <span className="text-amber-600 font-medium">Pending Split</span>;
    }

    // Paid By Logic
    let paidByDisplay = '-';
    if (expense.paidBy === 'club') {
      paidByDisplay = <span className="text-green-700 font-medium">Club</span>;
    } else {
      // Check for player name or explicit admin name
      const payerName = expense.addedByPlayerName || expense.paidByAdminName || (expense.paidBy !== 'player' ? expense.paidBy : null);

      if (payerName) {
        paidByDisplay = (
          <div className="flex flex-col">
            <span className="text-blue-700 font-medium">{payerName}</span>
            {expense.paidBy === 'admin' && expense.reimbursementStatus === 'pending' && (
              <span className="text-xs text-amber-600">Pending Reimbursement</span>
            )}
            {expense.paidBy === 'admin' && expense.reimbursementStatus === 'reimbursed' && (
              <span className="text-xs text-green-600">Reimbursed</span>
            )}
            {/* If paid be player and marked as 'pending' (though not explicitly 'admin' flow) */}
            {expense.paidBy === 'player' && expense.reimbursementStatus === 'pending' && (
              <span className="text-xs text-amber-600">Pending Reimbursement</span>
            )}
          </div>
        );
      } else if (expense.paidBy === 'player') {
        // Fallback if name missing but marked as player paid
        paidByDisplay = <span className="text-blue-700 font-medium">Player</span>;
      }
    }

    return (
      <React.Fragment key={expense.id}>
        <tr className={`hover: bg - gray - 50 border - b border - gray - 100 transition - colors ${isExpanded ? 'bg-gray-50' : ''} `}>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dateDisplay}</td>
          <td className="px-6 py-4 text-sm font-medium text-gray-900">
            <div className="flex items-center gap-2">
              {expense.description}
              {expense.receiptURL && (
                <a href={expense.receiptURL} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="View Receipt">
                  <Eye size={14} />
                </a>
              )}
            </div>
          </td>
          {/* Context Column (Player for donation, Match for others) */}
          <td className="px-6 py-4 text-sm text-gray-600 font-medium">{contextName}</td>

          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{formatCurrency(expense.amount)}</td>

          {/* Hide Paid By for Donation (redundant with Player/Context) */}
          {type !== 'donation' && <td className="px-6 py-4 text-sm text-gray-600">{paidByDisplay}</td>}

          {/* Hide Split for Donation */}
          {type !== 'donation' && <td className="px-6 py-4 text-sm">{splitSummary}</td>}

          <td className="px-6 py-4 whitespace-nowrap">
            {type === 'donation' ? (
              <span className="text-sm font-medium text-gray-700">
                {getDonationApprover(expense) || "N/A"}
              </span>
            ) : (
              renderStatusBadge(expense)
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <div className="flex items-center justify-end gap-2">
              {/* Actions */}
              {/* Reimburse */}
              {expense.paidBy === 'admin' && expense.reimbursementStatus === 'pending' && onMarkReimbursed && (
                <button onClick={() => onMarkReimbursed(expense)} className="p-1 text-amber-600 hover:text-amber-800" title="Reimburse">
                  <DollarSign size={16} />
                </button>
              )}
              {/* Approve/Deny - Only if pending and not own expense */}
              {expense.approvalStatus === 'pending' && expense.createdBy !== currentUserEmail && (
                <>
                  <button onClick={() => onApproveExpense(expense)} className="p-1 text-green-600 hover:text-green-800" title="Approve">
                    <CheckCircle size={16} />
                  </button>
                  <button onClick={() => onDenyExpense(expense)} className="p-1 text-red-600 hover:text-red-800" title="Deny">
                    <XCircle size={16} />
                  </button>
                </>
              )}
              {/* Settle - only for Tournament Fees */}
              {!isSettled && !expense.clubCovered && onSettleTournamentFee && expense.isTournamentFee && (
                <button onClick={() => onSettleTournamentFee(expense)} className="p-1 text-purple-600 hover:text-purple-800" title="Split Among Players">
                  <Users size={16} />
                </button>
              )}
              {/* Mark Club Covered */}
              {!isSettled && !expense.clubCovered && onMarkClubCovered && expense.isTournamentFee && (
                <button onClick={() => onMarkClubCovered(expense)} className="p-1 text-green-600 hover:text-green-800" title="Mark Club Covered">
                  <Trophy size={16} />
                </button>
              )}
              {/* Edit/Delete */}
              <button onClick={() => onEditExpense(expense)} className="p-1 text-blue-600 hover:text-blue-800" title="Edit">
                <Edit size={16} />
              </button>
              <button onClick={() => { if (confirm('Delete expense?')) onDeleteExpense(expense.id) }} className="p-1 text-red-600 hover:text-red-800" title="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </td>
        </tr>
        {/* Expanded Details Row - Hide for Donation */}
        {isExpanded && type !== 'donation' && (
          <tr className="bg-gray-50">
            <td colSpan="8" className="px-6 py-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-inner">
                <h4 className="font-semibold text-sm text-gray-900 mb-3">Split Breakdown</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {expense.splitAmongDetails ? (
                    expense.splitAmongDetails.sort((a, b) => b.gamesPlayed - a.gamesPlayed).map((p, idx) => {
                      const isExternal = isExternalPlayer(p.oderId);
                      const paymentRecord = expense.externalPayments?.[p.oderId];

                      return (
                        <div key={idx} className={`p - 2 rounded text - sm flex justify - between border ${isExternal ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'} `}>
                          <div>
                            <span className="font-medium">{p.name}</span>
                            {isExternal && <span className="ml-2 text-[10px] bg-amber-200 text-amber-800 px-1 rounded">External</span>}
                            <div className="text-xs text-gray-500">{p.gamesPlayed}g · {formatCurrency(p.amount)}</div>
                          </div>
                          {isExternal && (
                            <div className="flex items-center">
                              {paymentRecord ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-green-700 font-medium">✓ {paymentRecord.paymentMethod}</span>
                                  {onUnmarkExternalPlayerPaid && (
                                    <button onClick={() => onUnmarkExternalPlayerPaid(expense.id, p.oderId, p.name)} className="text-red-500 hover:text-red-700 ml-1">
                                      <XCircle size={12} />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                onMarkExternalPlayerPaid && (
                                  <button
                                    onClick={() => setPaymentModal({ expenseId: expense.id, playerId: p.oderId, playerName: p.name, amount: p.amount })}
                                    className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
                                  >
                                    Mark Paid
                                  </button>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    // Fallback for simple split
                    expense.splitAmong?.map((pid, idx) => {
                      const playerName = resolvePlayerName(pid);
                      return (
                        <div key={idx} className="p-2 border border-gray-200 rounded text-sm bg-gray-50">
                          {playerName}
                        </div>
                      )
                    })
                  )}
                </div>
                {expense.denialReason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex items-start gap-2">
                    <AlertCircle size={16} className="mt-0.5" />
                    <div>
                      <span className="font-bold">Denial Reason:</span> {expense.denialReason}
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderTable = (data, type, title, icon) => {
    if (data.length === 0) return null;
    return (
      <div className="bg-white rounded-xl shadow-sm mb-8 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center gap-2">
          <div className="p-2 bg-gray-100 rounded-full text-gray-600">{icon}</div>
          <h3 className="font-bold text-lg text-gray-900">{title}</h3>
          <span className="ml-auto text-sm text-gray-500">{data.length} records · {formatCurrency(data.reduce((s, e) => s + (e.amount || 0), 0))}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
              <tr>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Description</th>
                {/* Dynamic Header */}
                <th className="px-6 py-3">
                  {type === 'tournament' ? 'Tournament' : (type === 'donation' ? 'Player' : 'Match / Context')}
                </th>
                <th className="px-6 py-3">Amount</th>
                {type !== 'donation' && <th className="px-6 py-3">Paid By</th>}
                {type !== 'donation' && <th className="px-6 py-3">Split</th>}
                <th className="px-6 py-3">{type === 'donation' ? 'Approved By' : 'Status'}</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map(expense => renderExpenseRow(expense, type))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <button
        onClick={onBack}
        className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
      >
        ← Back to Overview
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="text-red-600" size={28} />
            Transactions Report
          </h2>
          <p className="text-sm text-gray-600 mt-1">Track and manage all club transactions and expenses</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Total Expenses</p>
          <p className="text-3xl font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
        </div>
      </div>

      {/* Tournament Filter */}
      <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
        <Filter className="text-gray-400" size={20} />
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Filter by Tournament</label>
          <select
            value={selectedTournament}
            onChange={(e) => setSelectedTournament(e.target.value)}
            className="w-full md:w-1/3 px-3 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Tournaments</option>
            {tournaments && tournaments.map(tournament => (
              <option key={tournament.id} value={tournament.id}>
                {tournament.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Transactions Categories */}
      <div className="space-y-8">

        {/* 1. Initial Donations Section */}
        {(() => {
          const initialDonations = filteredExpenses.filter(e => e.isInitialDonation || e.isRegistrationFee);
          return renderTable(initialDonations, 'donation', `${getRegistrationFeeLabel({ name: clubName })}s`, <DollarSign size={20} className="text-green-600" />);
        })()}

        {/* 2. Game Expenses Section */}
        {(() => {
          const gameExpenses = filteredExpenses.filter(e =>
            !(e.isInitialDonation || e.isRegistrationFee) && e.gameId
          );
          return renderTable(gameExpenses, 'game', 'Game Expenses', <Trophy size={20} className="text-blue-600" />);
        })()}

        {/* 3. Other Expenses Section */}
        {(() => {
          const otherExpenses = filteredExpenses.filter(e =>
            !(e.isInitialDonation || e.isRegistrationFee) && !e.gameId
          );
          return renderTable(otherExpenses, 'other', 'Other Expenses', <Calendar size={20} className="text-orange-600" />);
        })()}

      </div>

      {/* Charts Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <PieChart className="text-purple-600" size={24} />
            Expense Summary
          </h2>
          <button onClick={() => setShowCharts(!showCharts)} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            {showCharts ? 'Hide Charts' : 'Show Charts'}
          </button>
        </div>

        {showCharts && (() => {
          // Calculate expense data by category
          const categoryData = {};
          const playerExpenseData = {};

          // Process all expenses
          filteredExpenses.forEach(expense => {
            if (expense.isRegistrationFee || expense.isInitialDonation) return;

            const category = expense.isTournamentFee
              ? 'Tournament Fees'
              : (expense.description || expense.type || 'Other');

            // Add to category totals
            if (!categoryData[category]) {
              categoryData[category] = 0;
            }
            categoryData[category] += expense.amount || 0;

            // Calculate player shares
            if (expense.splitAmongDetails && expense.splitAmongDetails.length > 0) {
              // Tournament fees with detailed breakdown
              expense.splitAmongDetails.forEach(player => {
                if (!playerExpenseData[player.oderId]) {
                  playerExpenseData[player.oderId] = {
                    name: player.name,
                    total: 0,
                    categories: {},
                    isExternal: player.isExternal || isExternalPlayer(player.oderId)
                  };
                }
                playerExpenseData[player.oderId].total += player.amount || 0;
                if (!playerExpenseData[player.oderId].categories[category]) {
                  playerExpenseData[player.oderId].categories[category] = 0;
                }
                playerExpenseData[player.oderId].categories[category] += player.amount || 0;
              });
            } else if (expense.splitAmong && expense.splitAmong.length > 0) {
              // Regular game expenses
              const perPlayerAmount = (expense.amount || 0) / expense.splitAmong.length;
              expense.splitAmong.forEach(playerId => {
                // Get player name
                let playerName = 'Unknown';
                let isExternal = isExternalPlayer(playerId);

                // 1. Try finding in Teams (Regular Players)
                if (teams) {
                  for (const team of teams) {
                    if (team.players) {
                      const player = team.players.find(p => p.id === playerId || p.userId === playerId);
                      if (player) {
                        playerName = player.name;
                        isExternal = player.isExternal || player.isManual || player.isGuest || isExternal;
                        break;
                      }
                    }
                  }
                }

                // 2. Fallback: Try finding in Games (Guest/External players for specific games)
                if (playerName === 'Unknown' && games) {
                  for (const game of games) {
                    if (game.guestPlayers) {
                      const guest = game.guestPlayers.find(g => g.id === playerId);
                      if (guest) {
                        playerName = guest.name;
                        isExternal = true;
                        break;
                      }
                    }
                  }
                }

                if (!playerExpenseData[playerId]) {
                  playerExpenseData[playerId] = {
                    name: playerName,
                    total: 0,
                    categories: {},
                    isExternal
                  };
                }
                playerExpenseData[playerId].total += perPlayerAmount;
                if (!playerExpenseData[playerId].categories[category]) {
                  playerExpenseData[playerId].categories[category] = 0;
                }
                playerExpenseData[playerId].categories[category] += perPlayerAmount;
              });
            }
          });

          // Convert and sort
          const categoryArray = Object.entries(categoryData)
            .map(([name, amount]) => ({ name, amount }))
            .sort((a, b) => b.amount - a.amount);

          const playerArray = Object.entries(playerExpenseData)
            .map(([id, data]) => ({ id, ...data }))
            .sort((a, b) => b.total - a.total);

          const totalExpenseAmount = categoryArray.reduce((sum, c) => sum + c.amount, 0);

          // Pie Chart Colors
          const colors = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#6366F1', '#14B8A6', '#F97316', '#84CC16'];

          // Simple SVG Pie Chart
          const PieChartSVG = ({ data, size = 200 }) => {
            if (!data || data.length === 0) return null;
            const total = data.reduce((sum, d) => sum + d.amount, 0);
            if (total === 0) return null;
            let currentAngle = 0;
            const radius = size / 2 - 10;
            const centerX = size / 2;
            const centerY = size / 2;

            return (
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size} `}>
                {data.map((item, index) => {
                  const percentage = item.amount / total;
                  const angle = percentage * 360;
                  const startAngle = currentAngle;
                  const endAngle = currentAngle + angle;
                  currentAngle = endAngle;

                  const startRad = (startAngle - 90) * Math.PI / 180;
                  const endRad = (endAngle - 90) * Math.PI / 180;
                  const x1 = centerX + radius * Math.cos(startRad);
                  const y1 = centerY + radius * Math.sin(startRad);
                  const x2 = centerX + radius * Math.cos(endRad);
                  const y2 = centerY + radius * Math.sin(endRad);
                  const largeArc = angle > 180 ? 1 : 0;
                  const pathD = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

                  return <path key={index} d={pathD} fill={colors[index % colors.length]} stroke="white" strokeWidth="2" />;
                })}
              </svg>
            );
          };

          const selectedPlayerData = selectedPlayerForChart !== 'all' ? playerExpenseData[selectedPlayerForChart] : null;

          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Category Breakdown */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign size={18} className="text-green-600" /> Expense by Category
                </h3>
                {categoryArray.length === 0 ? <p className="text-gray-500 text-sm">No expenses</p> : (
                  <div className="flex flex-col items-center">
                    <PieChartSVG data={categoryArray} size={180} />
                    <div className="mt-4 w-full space-y-2">
                      {categoryArray.map((cat, idx) => (
                        <div key={cat.name} className="flex justify-between text-sm">
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[idx % colors.length] }} />{cat.name}</div>
                          <span className="font-semibold">{formatCurrency(cat.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Player Breakdown */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users size={18} className="text-blue-600" /> Expense by Player
                </h3>
                <select value={selectedPlayerForChart} onChange={e => setSelectedPlayerForChart(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm mb-4">
                  <option value="all">All Players - Rankings</option>
                  {playerArray.map(p => <option key={p.id} value={p.id}>{p.name} - {formatCurrency(p.total)}</option>)}
                </select>
                {selectedPlayerForChart === 'all' ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {playerArray.map((p, idx) => (
                      <div key={p.id} className="flex justify-between p-2 rounded bg-gray-50">
                        <span className="text-sm">{idx + 1}. {p.name} {p.isExternal && '(Ext)'}</span>
                        <span className="font-semibold text-purple-700">{formatCurrency(p.total)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  selectedPlayerData && (
                    <div className="flex flex-col items-center">
                      <p className="font-bold text-lg mb-2">{selectedPlayerData.name}</p>
                      <p className="font-bold text-2xl text-purple-700">{formatCurrency(selectedPlayerData.total)}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Payment Modal */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded"><span className="text-sm text-gray-500">Player:</span> <span className="font-bold">{paymentModal.playerName}</span></div>
              <div className="p-3 bg-green-50 rounded border border-green-200"><span className="text-sm text-gray-500">Amount:</span> <span className="font-bold text-xl text-green-700">{formatCurrency(paymentModal.amount)}</span></div>
              <div>
                <label className="block text-sm font-medium mb-2">Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {['Cash', 'Venmo', 'Zelle'].map(m => (
                    <button key={m} onClick={() => setPaymentMethod(m.toLowerCase())} className={`py - 2 border rounded ${paymentMethod === m.toLowerCase() ? 'bg-green-100 border-green-500 text-green-800' : 'hover:bg-gray-50'} `}>{m}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setPaymentModal(null)} className="flex-1 py-2 border rounded hover:bg-gray-50">Cancel</button>
              <button onClick={() => { onMarkExternalPlayerPaid(paymentModal.expenseId, paymentModal.playerId, paymentModal.playerName, paymentModal.amount, paymentMethod); setPaymentModal(null); }} className="flex-1 py-2 bg-green-600 text-white rounded hover:bg-green-700">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseReport;